import { Loading, MessageBox } from "element-ui";
import {
  useShowErrorMessage,
  useShowSuccessMessage,
} from "../use-notifications";
import useHttpRequest from "../use-http-request";
import { useRouter } from "vue-router/composables";
import { Ref } from "vue";
import { DraftStorage } from "@/helpers/onlineAnswerSheet/draftStorage";
import Examination from "@/interfaces/examination.interface";

export const useSubmitExamination = ({
  examination,
  saveStudentChanges,
  saveStudentFinalChanges,
  isUploadAnswerSheetDialogVisible,
  submissionType,
  draftStorage,
}: {
  examination: Ref<Examination | null>;
  saveStudentChanges: () => Promise<string | null>;
  saveStudentFinalChanges: () => Promise<boolean | null>;
  isUploadAnswerSheetDialogVisible: Ref<boolean>;
  submissionType: Ref<"Online" | "File Upload" | undefined>;
  draftStorage: Ref<DraftStorage | undefined>;
}) => {
  const router = useRouter();

  const onSubmitAnswerSheetIntent = () => {
    if (!examination.value?.is_answersheet_file_upload_allowed) {
      submissionType.value = "Online";
      isUploadAnswerSheetDialogVisible.value = true;
      return;
    }

    MessageBox.alert(
      "Please Choose a method of submitting your answer sheet, you can either upload the Answer Sheet file you downloaded or Submit the Online Answer sheet.",
      "Choose Submission Method",
      {
        confirmButtonText: "Online Answers",
        cancelButtonText: "Upload File",
        showCancelButton: true,
        distinguishCancelAndClose: true,
        closeOnClickModal: true,
        closeOnPressEscape: true,

        // type: "warning",
        callback: (action) => {
          if (action == "confirm") {
            // this.uploadType = "ONLINE";
            submissionType.value = "Online";
            isUploadAnswerSheetDialogVisible.value = true;
          } else if (action == "cancel") {
            submissionType.value = "File Upload";
            isUploadAnswerSheetDialogVisible.value = true;
          }
        },
      }
    );
  };

  const onSubmitOnlineAnswerSheet = async () => {
    let loadingIndicator: ReturnType<typeof Loading.service> | undefined;
    try {
      loadingIndicator = Loading.service({
        lock: true,
        text: "Submitting...",
        background: "rgba(0, 0, 0, 0.7)",
      });

      const isSaved = await saveStudentChanges();

      if (!isSaved) {
        return useShowErrorMessage(
          "Unable to Save Changes",
          "An unexpected Error occurred, please try again"
        );
      }

      const finalChangesSaved = await saveStudentFinalChanges();

      if (!finalChangesSaved) {
        return useShowErrorMessage(
          "Unable to Save Changes",
          "An unexpected Error occurred, please try again"
        );
      }

      const request = await useHttpRequest<{
        success: boolean;
        message: string;
        should_ask_for_feedback: boolean;
        student_examination_submission_id: string;
      }>({
        method: "POST",
        url: "examinations/submit-online-answer-sheet",
        body: {
          examination_id: examination.value?.examination_id,
        },
        loadingProperty: null,
        showSuccessMessage: false,
        errorLoadingProperty: null,
        showFullScreenLoader: false,
      });

      if (!request) {
        return;
      }

      if (
        request.success &&
        request.message == "ONLINE ANSWER SHEET SUBMITTED SUCCESSFULLY"
      ) {
        useShowSuccessMessage(
          "Exam Submitted",
          "Examination Answers successfully submitted"
        );

        draftStorage.value?.clear();

        // this.$router.replace({ name: "Examinations" });
        router.push({
          name: "Examinations",
          params: {
            submitted_examination_id: request.student_examination_submission_id,
            should_ask_for_feedback:
              request.should_ask_for_feedback as unknown as string,
          },
        });
      }
    } catch (error) {
      //Do nothing for now
    } finally {
      if (loadingIndicator) {
        loadingIndicator.close();
      }
    }
  };

  const onSubmitFileUploadAnswerSheet = async (path: {
    url: string;
    key: string;
    file_name: string;
  }) => {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      should_ask_for_feedback: boolean;
      student_examination_submission_id: string;
    }>({
      method: "POST",
      url: "examinations/submit-file-upload-answer-sheet",
      body: {
        examination_id: examination.value?.examination_id,
        answer_sheet_path_url: path.url,
        answer_sheet_path_key: path.key,
        file_name: path.file_name,
      },
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
      showFullScreenLoader: true,
      fullScreenLoaderMessage: "Submitting...",
    });

    if (!request) {
      return;
    }

    if (
      request.success &&
      request.message == "ANSWER SHEET SUBMITTED SUCCESSFULLY"
    ) {
      useShowSuccessMessage(
        "Exam Submitted",
        "Examination Answers successfully submitted"
      );

      draftStorage.value?.clear();

      router.push({
        name: "Examinations",
        params: {
          submitted_examination_id: request.student_examination_submission_id,
          should_ask_for_feedback:
            request.should_ask_for_feedback as unknown as string,
        },
      });
    }
  };

  return {
    onSubmitOnlineAnswerSheet,
    onSubmitFileUploadAnswerSheet,
    onSubmitAnswerSheetIntent,
  };
};
